import renderMathInElement from "katex/contrib/auto-render";
import { escapeHtml } from "./escapeHtml.js";
import { markdownToSafeHtml, markdownToSafeHtmlMaxProse } from "./markdownPreview.js";
import {
  MAX_MAX_AUDIO_HEX_PREFIX,
  MAX_MAX_VIDEO_HEX_PREFIX,
} from "./maxBrowseHexMediaSupport.js";
import { mountMaxPdfRasterImages } from "./maxPdfPageRaster.js";
import { hex64ToBytes } from "./nullaCodec.js";
import { evaluateMaxNumericExpression, type MaxCalcTables, type MaxCalcUserFuncs } from "./maxCalcExpr.js";
import {
  accumulateMaxGraphEdges,
  applyMaxGraphOrientation,
  applyMaxNodeColors,
  buildMaxGraphAscii,
  buildMaxGraphAsciiText,
  buildMaxGraphSvg,
  type MaxGraphs,
  type MaxGraphOrientations,
  type MaxNodeColors,
} from "./maxGraphPreview.js";
import {
  isDiagramDefBody,
  isDiagramRefBody,
  parseDiagramBody,
  buildDrawioXml,
  buildDiagramSvgSync,
  type MaxDiagrams,
} from "./diagramDsl.js";

export { evaluateMaxNumericExpression };

/** Tags KaTeX skips; omit `pre` — we use mixed block markup. */
const KATEX_AUTO_RENDER_IGNORED_TAGS = [
  "script",
  "noscript",
  "style",
  "textarea",
  "code",
  "option",
  "video",
  "img",
] as const;

const MAX_BRACE_RECURSE = 48;

/**
 * If the calc body is `{…}` (balanced) with only optional `;` after the closing `}`, return inner
 * source and whether the outer block suppresses visible output (trailing `;`).
 */
export function tryExtractBraceWrappedMaxBody(body: string): { inner: string; suppress: boolean } | null {
  const s = body.trim();
  if (!s.startsWith("{")) return null;
  let depth = 1;
  let inStr = false;
  let strEsc = false;
  for (let i = 1; i < s.length; i++) {
    const c = s[i]!;
    if (inStr) {
      if (strEsc) strEsc = false;
      else if (c === "\\") strEsc = true;
      else if (c === '"') inStr = false;
      continue;
    }
    if (c === '"') {
      inStr = true;
      continue;
    }
    if (c === "{") depth++;
    else if (c === "}") {
      depth--;
      if (depth === 0) {
        const tail = s.slice(i + 1).trim();
        if (tail !== "" && !/^;+$/.test(tail)) return null;
        return { inner: s.slice(1, i), suppress: tail.length > 0 };
      }
      if (depth < 0) return null;
    }
  }
  return null;
}

type MaxSegment =
  | { kind: "text"; body: string }
  | { kind: "math"; body: string }
  | { kind: "calc"; body: string }
  | { kind: "graph"; body: string };

/**
 * Max source text segment that is only `name = ` before @@…@@ on the next line (optional GFM bold
 * or underscore emphasis around the name).
 */
function maxFlowSourceAssignmentWholeLine(textSegBody: string): boolean {
  const t = textSegBody.trim();
  const stripped = t
    .replace(/^\*{1,2}([\s\S]*?)\*{1,2}\s*=\s*$/u, "$1 = ")
    .replace(/^_{1,2}([\s\S]*?)_{1,2}\s*=\s*$/u, "$1 = ");
  return /^[a-zA-Z][a-zA-Z0-9_]*\s*=\s*$/u.test(stripped.trim());
}

/**
 * Assignment-only flow lines (`name = ` / emphasis variants): bypass Markdown (`breaks` → `<br>`)
 * and emit fixed HTML with NBSP glue so preview cannot split label from the following `@@` calc.
 */
function maxFlowAssignmentLeadHtmlFromSource(body: string): string | null {
  if (!maxFlowSourceAssignmentWholeLine(body)) return null;
  const t = body.trim();
  let name: string;
  let wrap: "bold" | "italic" | "none";

  const mBoldStar = /^\*{2}([\s\S]*?)\*{2}\s*=\s*$/u.exec(t);
  const mBoldUs = /^_{2}([\s\S]*?)_{2}\s*=\s*$/u.exec(t);
  const mItStar = /^\*([\s\S]*?)\*\s*=\s*$/u.exec(t);
  const mItUs = /^_([\s\S]*?)_\s*=\s*$/u.exec(t);
  const mPlain = /^([a-zA-Z][a-zA-Z0-9_]*)\s*=\s*$/u.exec(t);

  if (mBoldStar) {
    name = mBoldStar[1]!.trim();
    wrap = "bold";
  } else if (mBoldUs) {
    name = mBoldUs[1]!.trim();
    wrap = "bold";
  } else if (mItStar) {
    name = mItStar[1]!.trim();
    wrap = "italic";
  } else if (mItUs) {
    name = mItUs[1]!.trim();
    wrap = "italic";
  } else if (mPlain) {
    name = mPlain[1]!;
    wrap = "none";
  } else {
    return null;
  }

  if (!/^[a-zA-Z][a-zA-Z0-9_]*$/.test(name)) return null;
  const esc = escapeHtml(name);
  if (wrap === "bold") return `<strong>${esc}</strong>\u00A0=\u00A0`;
  if (wrap === "italic") return `<em>${esc}</em>\u00A0=\u00A0`;
  return `${esc}\u00A0=\u00A0`;
}

/**
 * True when prose can share one visual line with a neighboring `@@…@@` calc. Single newlines are
 * disallowed except (a) no newline at all, or (b) one trailing newline after a “wrap point”
 * (`:.;!?` or `)` or closing `$`) so `…$x$:\n@@…@@` does not split into stacked blocks,
 * or (c) the prose is only `name = ` with optional trailing whitespace/newlines so `name = \n@@…@@` stays inline.
 */
export function maxTextSegmentUsesFlowLayout(body: string): boolean {
  const leading = body.replace(/^\s*\r?\n+/, "");
  if (leading.length === 0) return false;
  if (/\r?\n\s*\r?\n/.test(leading)) return false;
  if (!/\r?\n/.test(leading)) return true;
  const chopTrailingWsNl = leading.replace(/(?:\s|\r?\n)+$/u, "");
  if (maxFlowSourceAssignmentWholeLine(chopTrailingWsNl)) return true;
  return /[:.;!?]\s*\r?\n\s*$/.test(leading) || /\)\s*\r?\n\s*$/.test(leading) || /\$\s*\r?\n\s*$/.test(leading);
}

function maxTextSegmentEndsWithParagraphBreak(body: string): boolean {
  return /\r?\n\s*\r?\n\s*$/.test(body);
}

function maxTextSegmentStartsWithParagraphBreak(body: string): boolean {
  return /^\s*\r?\n\s*\r?\n/.test(body);
}

/**
 * For each segment index, whether that `@@…@@` calc should use inline flow (same line as
 * single-line prose). Calc runs touching single-line text or a document that is only calcs use flow.
 */
export function maxCalcFlowLayoutByIndex(segs: readonly MaxSegment[]): boolean[] {
  const n = segs.length;
  const out = segs.map(() => false);
  for (let i = 0; i < n; i++) {
    if (segs[i]!.kind !== "calc") continue;
    let j = i;
    while (j < n && segs[j]!.kind === "calc") j++;
    const left = i > 0 ? segs[i - 1]! : undefined;
    const right = j < n ? segs[j]! : undefined;
    const leftParagraphBreak =
      left?.kind === "text" && maxTextSegmentEndsWithParagraphBreak(left.body);
    const touchesFlowText =
      (left?.kind === "text" && maxTextSegmentUsesFlowLayout(left.body)) ||
      (right?.kind === "text" && maxTextSegmentUsesFlowLayout(right.body) && !leftParagraphBreak);
    const soleDocRun = i === 0 && j === n;
    if (touchesFlowText || soleDocRun) {
      for (let k = i; k < j; k++) out[k] = true;
    }
    i = j - 1;
  }
  return out;
}

/**
 * Marked wraps plain text in one root `<p>…</p>`. That `<p>` is block-level and still forces the
 * next `@@` span onto a new line even when the prose wrapper is `inline-block`. For flow segments,
 * strip a single outer `<p>` so phrasing content sits in the wrapper directly (still sanitized).
 */
export function unwrapMaxFlowMarkdownSingleParagraph(html: string): string {
  const t = html.trim();
  // Inner must not contain `</p>` or a second paragraph would satisfy `$` via the last `</p>`.
  const m = /^<p(\s[^>]*)?>((?:(?!<\/p>)[\s\S])*)<\/p>\s*$/i.exec(t);
  return m ? m[2]! : html;
}

/**
 * Max prose markdown uses `breaks: true`, so a trailing newline after `name = ` becomes `<br>`.
 * When that text flows on one row with the following `@@…@@`, strip trailing hard breaks so we do not force a line between `=` and the result.
 */
function stripTrailingMarkedHardBreaks(html: string): string {
  return html.replace(/(?:\s|(?:<br\s*\/?>))+\s*$/gi, "");
}

/**
 * Flow prose is parsed separately from adjacent `@@...@@` segments, so edge spaces can disappear
 * (e.g. `a @@x@@` losing the gap before calc). Keep a single visible boundary space when the
 * original source segment starts/ends with whitespace.
 */
function preserveMaxFlowEdgeSpaces(sourceText: string, html: string): string {
  let out = html;
  // Leading space is already preserved by marked for inline text fragments like " a".
  if (/\s$/.test(sourceText)) out = `${out}&nbsp;`;
  return out;
}

function preservePostCalcLeadingBlankLine(sourceText: string, html: string): string {
  if (!/^\s*\r?\n\s*\r?\n/.test(sourceText)) return html;
  return `<p><br></p>${html}`;
}

type MaxDrawSpec =
  | { mode: "fixed"; varName: string; loExpr: string; hiExpr: string; fnExpr: string }
  | { mode: "auto"; varName: string; fnExpr: string };

function parseMaxDrawDirective(inner: string): MaxDrawSpec | null {
  const src = inner.trim().replace(/;+\s*$/g, "");
  const fixed = /^draw\s*\{\s*([a-zA-Z][a-zA-Z0-9]*)\s*=\s*([\s\S]+?)\s*\.\.\s*([\s\S]+?)\s*\}\s*\{([\s\S]+)\}\s*$/u.exec(
    src,
  );
  if (fixed) {
    return {
      mode: "fixed",
      varName: fixed[1]!,
      loExpr: fixed[2]!.trim(),
      hiExpr: fixed[3]!.trim(),
      fnExpr: fixed[4]!.trim(),
    };
  }
  const auto = /^draw\s*\{\s*([a-zA-Z][a-zA-Z0-9]*)\s*->\s*auto\s*\}\s*\{([\s\S]+)\}\s*$/u.exec(src);
  if (auto) {
    return { mode: "auto", varName: auto[1]!, fnExpr: auto[2]!.trim() };
  }
  return null;
}

type MaxAsciiDrawSpec = MaxDrawSpec & { plotChar: string };

// Optional third brace group: {char} — any single printable character used as the plot point.
// Examples:
//   @@ascii-draw{x->auto}{sin(x)}@@        → default ·
//   @@ascii-draw{x->auto}{sin(x)}{*}@@     → asterisk
//   @@ascii-draw{x=-3..3}{cos(x)}{o}@@     → letter o
function parseMaxAsciiDrawDirective(inner: string): MaxAsciiDrawSpec | null {
  const src = inner.trim().replace(/;+\s*$/g, "");
  // Match an optional trailing {char} group after the expression block.
  const charSuffix = /\}\s*\{([^\s{}])\}\s*$/.exec(src);
  const plotChar = charSuffix ? charSuffix[1]! : "·";
  // Strip the optional {char} suffix before matching the main pattern.
  const core = charSuffix ? src.slice(0, src.lastIndexOf("{" + charSuffix[1]!)).trimEnd() : src;

  const fixed =
    /^ascii-draw\s*\{\s*([a-zA-Z][a-zA-Z0-9]*)\s*=\s*([\s\S]+?)\s*\.\.\s*([\s\S]+?)\s*\}\s*\{([\s\S]+)\}\s*$/u.exec(
      core,
    );
  if (fixed) {
    return {
      mode: "fixed",
      varName: fixed[1]!,
      loExpr: fixed[2]!.trim(),
      hiExpr: fixed[3]!.trim(),
      fnExpr: fixed[4]!.trim(),
      plotChar,
    };
  }
  const auto =
    /^ascii-draw\s*\{\s*([a-zA-Z][a-zA-Z0-9]*)\s*->\s*auto\s*\}\s*\{([\s\S]+)\}\s*$/u.exec(core);
  if (auto) {
    return { mode: "auto", varName: auto[1]!, fnExpr: auto[2]!.trim(), plotChar };
  }
  return null;
}

function evalNumericForDraw(
  expr: string,
  vars: Record<string, number>,
  tables: MaxCalcTables,
  funcs?: MaxCalcUserFuncs,
): number | null {
  const ev = evaluateMaxNumericExpression(expr, vars, tables, funcs);
  if (!ev.ok) return null;
  const n = Number(ev.value);
  if (!Number.isFinite(n)) return null;
  return n;
}

function buildMaxDrawSvg(
  spec: MaxDrawSpec,
  vars: Record<string, number>,
  tables: MaxCalcTables,
): string {
  const width = 560;
  const height = 300;
  const padL = 46;
  const padR = 14;
  const padT = 12;
  const padB = 28;
  const plotW = width - padL - padR;
  const plotH = height - padT - padB;
  const withVar = (x: number): Record<string, number> => ({ ...vars, [spec.varName]: x });

  let xMin = -10;
  let xMax = 10;
  if (spec.mode === "fixed") {
    const lo = evalNumericForDraw(spec.loExpr, vars, tables);
    const hi = evalNumericForDraw(spec.hiExpr, vars, tables);
    if (lo === null || hi === null) {
      return `<div class="npp-max-draw npp-max-draw--err">draw: invalid x bounds</div>`;
    }
    xMin = Math.min(lo, hi);
    xMax = Math.max(lo, hi);
  }
  if (!(xMax > xMin)) {
    return `<div class="npp-max-draw npp-max-draw--err">draw: x range must be non-zero</div>`;
  }

  const samples = 220;
  const pts: Array<{ x: number; y: number }> = [];
  for (let i = 0; i <= samples; i++) {
    const t = i / samples;
    const x = xMin + (xMax - xMin) * t;
    const y = evalNumericForDraw(spec.fnExpr, withVar(x), tables);
    if (y !== null) pts.push({ x, y });
  }
  if (!pts.length) {
    return `<div class="npp-max-draw npp-max-draw--err">draw: function has no finite points in range</div>`;
  }

  let yMin = Math.min(...pts.map((p) => p.y));
  let yMax = Math.max(...pts.map((p) => p.y));
  if (spec.mode === "auto") {
    xMin = Math.min(...pts.map((p) => p.x));
    xMax = Math.max(...pts.map((p) => p.x));
  }
  if (yMin === yMax) {
    yMin -= 1;
    yMax += 1;
  } else {
    const pad = (yMax - yMin) * 0.08;
    yMin -= pad;
    yMax += pad;
  }

  const sx = (x: number): number => padL + ((x - xMin) / (xMax - xMin)) * plotW;
  const sy = (y: number): number => padT + ((yMax - y) / (yMax - yMin)) * plotH;

  const d = pts
    .map((p, i) => `${i === 0 ? "M" : "L"}${sx(p.x).toFixed(2)},${sy(p.y).toFixed(2)}`)
    .join(" ");
  const x0In = xMin <= 0 && xMax >= 0;
  const y0In = yMin <= 0 && yMax >= 0;
  const xAxisY = y0In ? sy(0) : sy(yMin);
  const yAxisX = x0In ? sx(0) : sx(xMin);
  const xLabel = `${spec.varName} in [${xMin.toPrecision(4)}, ${xMax.toPrecision(4)}]`;
  const yLabel = `f range [${yMin.toPrecision(4)}, ${yMax.toPrecision(4)}]`;

  return (
    `<div class="npp-max-draw">` +
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" width="${width}" height="${height}" class="npp-max-draw-svg" aria-label="draw plot">` +
    `<rect x="${padL}" y="${padT}" width="${plotW}" height="${plotH}" fill="#fff" stroke="#d7e2ee"/>` +
    `<line x1="${padL}" y1="${xAxisY}" x2="${padL + plotW}" y2="${xAxisY}" stroke="#8aa0b8" stroke-width="1"/>` +
    `<line x1="${yAxisX}" y1="${padT}" x2="${yAxisX}" y2="${padT + plotH}" stroke="#8aa0b8" stroke-width="1"/>` +
    `<path d="${d}" fill="none" stroke="#205ecf" stroke-width="2"/>` +
    `<text x="${padL}" y="${height - 8}" class="npp-max-draw-label">${escapeHtml(xLabel)}</text>` +
    `<text x="${padL}" y="14" class="npp-max-draw-label">${escapeHtml(yLabel)}</text>` +
    `</svg>` +
    `</div>`
  );
}

/** Shared computation for both the HTML preview and plain-text export. Returns raw text lines. */
function buildMaxAsciiPlotRaw(
  spec: MaxAsciiDrawSpec,
  vars: Record<string, number>,
  tables: MaxCalcTables,
): { ok: true; text: string } | { ok: false; error: string } {
  const plotChar = spec.plotChar;
  const PLOT_W = 54; // columns in the plot area (= number of x samples)
  const PLOT_H = 12; // rows in the plot area
  const LABEL_W = 7; // left margin width: 6 chars + 1 separator space

  const withVar = (x: number): Record<string, number> => ({ ...vars, [spec.varName]: x });

  let xMin = -10;
  let xMax = 10;
  if (spec.mode === "fixed") {
    const lo = evalNumericForDraw(spec.loExpr, vars, tables);
    const hi = evalNumericForDraw(spec.hiExpr, vars, tables);
    if (lo === null || hi === null) {
      return { ok: false, error: "ascii-draw: invalid x bounds" };
    }
    xMin = Math.min(lo, hi);
    xMax = Math.max(lo, hi);
  }
  if (!(xMax > xMin)) {
    return { ok: false, error: "ascii-draw: x range must be non-zero" };
  }

  // Sample the function at PLOT_W+1 x values.
  const pts: Array<{ xi: number; y: number }> = [];
  for (let i = 0; i <= PLOT_W; i++) {
    const x = xMin + ((xMax - xMin) * i) / PLOT_W;
    const y = evalNumericForDraw(spec.fnExpr, withVar(x), tables);
    if (y !== null) pts.push({ xi: i, y });
  }
  if (pts.length === 0) {
    return { ok: false, error: "ascii-draw: no finite values in range" };
  }

  if (spec.mode === "auto") {
    // Re-derive x range from where the function is actually finite.
    xMin = xMin + ((xMax - xMin) * Math.min(...pts.map((p) => p.xi))) / PLOT_W;
    xMax = xMin + ((xMax - xMin) * Math.max(...pts.map((p) => p.xi))) / PLOT_W;
  }

  // Determine y range with a small padding so the curve doesn't touch the top/bottom edges.
  let yMin = Math.min(...pts.map((p) => p.y));
  let yMax = Math.max(...pts.map((p) => p.y));
  if (yMin === yMax) {
    yMin -= 1;
    yMax += 1;
  } else {
    const pad = (yMax - yMin) * 0.06;
    yMin -= pad;
    yMax += pad;
  }

  // Map a y value to a row index: 0 = top (yMax), PLOT_H-1 = bottom (yMin).
  const yToRow = (y: number) =>
    Math.min(PLOT_H - 1, Math.max(0, Math.round(((yMax - y) / (yMax - yMin)) * (PLOT_H - 1))));

  const totalW = LABEL_W + 1 + PLOT_W + 2; // label | plot ▶x
  const grid: string[][] = Array.from({ length: PLOT_H }, () => Array(totalW).fill(" "));
  const gset = (col: number, row: number, ch: string) => {
    if (col >= 0 && col < totalW && row >= 0 && row < PLOT_H) grid[row]![col] = ch;
  };

  // Y-axis.
  for (let r = 0; r < PLOT_H; r++) gset(LABEL_W, r, "│");

  // X-axis at y = 0 (if in range).
  const xAxisRow = yToRow(0);
  const hasXAxis = yMin < 0 && yMax > 0;
  if (hasXAxis) {
    for (let c = LABEL_W + 1; c <= LABEL_W + PLOT_W; c++) gset(c, xAxisRow, "─");
    gset(LABEL_W, xAxisRow, "┼");
    gset(LABEL_W + PLOT_W + 1, xAxisRow, "▶");
    gset(LABEL_W + PLOT_W + 2, xAxisRow, "x");
  } else {
    // Arrow at far right of the centre row.
    const midRow = Math.floor(PLOT_H / 2);
    gset(LABEL_W + PLOT_W + 1, midRow, "▶");
    gset(LABEL_W + PLOT_W + 2, midRow, "x");
  }

  // Plot the sampled points.
  for (const { xi, y } of pts) {
    const col = LABEL_W + 1 + xi;
    const row = yToRow(y);
    const cur = grid[row]![col];
    if (cur === "─") {
      gset(col, row, "┼"); // point intersects x-axis
    } else if (cur === " ") {
      gset(col, row, plotChar);
    }
    // If a point already drawn here, leave it.
  }

  // Y-axis labels: 5 evenly spaced ticks.
  const formatPlotNum = (v: number, w: number): string => {
    if (!Number.isFinite(v)) return "?".padStart(w);
    const candidates = [
      v.toPrecision(3),
      v.toFixed(2),
      v.toFixed(1),
      v.toFixed(0),
      v.toExponential(1),
    ];
    for (const s of candidates) {
      if (s.length <= w) return s.padStart(w);
    }
    return v.toFixed(0).slice(0, w).padStart(w);
  };

  const NUM_TICKS = 5;
  for (let ti = 0; ti < NUM_TICKS; ti++) {
    const r = Math.round((ti / (NUM_TICKS - 1)) * (PLOT_H - 1));
    const yVal = yMax - (r / (PLOT_H - 1)) * (yMax - yMin);
    const label = formatPlotNum(yVal, LABEL_W - 1);
    for (let i = 0; i < label.length; i++) gset(i, r, label[i]!);
  }

  // X-axis labels at the bottom.
  const xLabelLeft = formatPlotNum(xMin, 6);
  const xLabelRight = formatPlotNum(xMax, 6);
  const xLine =
    " ".repeat(LABEL_W + 1) + xLabelLeft + " ".repeat(PLOT_W - xLabelLeft.length - xLabelRight.length) + xLabelRight;

  const lines = grid.map((row) => row.join("").trimEnd());
  while (lines.length > 0 && lines[lines.length - 1] === "") lines.pop();
  lines.push(xLine.trimEnd());

  return { ok: true, text: lines.join("\n") };
}

function buildMaxAsciiPlot(
  spec: MaxAsciiDrawSpec,
  vars: Record<string, number>,
  tables: MaxCalcTables,
): string {
  const result = buildMaxAsciiPlotRaw(spec, vars, tables);
  if (!result.ok) return `<pre class="npp-max-graph-ascii">${escapeHtml(result.error)}</pre>`;
  return `<pre class="npp-max-graph-ascii">${escapeHtml(result.text)}</pre>`;
}

/** Plain-text ASCII plot — used by the Markdown export. Returns the raw text (no HTML). */
function buildMaxAsciiPlotText(
  spec: MaxAsciiDrawSpec,
  vars: Record<string, number>,
  tables: MaxCalcTables,
): string {
  const result = buildMaxAsciiPlotRaw(spec, vars, tables);
  return result.ok ? result.text : result.error;
}

/** GitHub-style pipe table; first row is the header (GFM requires one). */
export function maxTableGridToMarkdown(grid: string[][]): string {
  if (grid.length === 0) return "";
  const cols = grid[0]!.length;
  const esc = (c: string) => c.replace(/\\/g, "\\\\").replace(/\|/g, "\\|").replace(/\r\n|\n|\r/g, "<br>");
  const rowLine = (cells: readonly string[]) => "| " + cells.map((x) => esc(x)).join(" | ") + " |";
  const lines: string[] = [];
  lines.push(rowLine(grid[0]!));
  lines.push("| " + Array.from({ length: cols }, () => "---").join(" | ") + " |");
  for (let r = 1; r < grid.length; r++) {
    lines.push(rowLine(grid[r]!));
  }
  return lines.join("\n");
}

/** `@@name@@` / `@@name;@@` when `name` is a Max table → render as markdown table HTML. */
function maxTableOnlyRefName(inner: string): string | null {
  const m = /^([a-zA-Z_][a-zA-Z0-9_]*)\s*(?:;+\s*)*$/u.exec(inner.trim());
  return m?.[1] ?? null;
}

const MAX_NESTED_CALC_EXPAND = 64;

/**
 * Index of the closing `@@` for a calc block opened at `openAt` (first `@` of the opener).
 * Ignores `@@` pairs that sit inside `{…}` so `@@{ … @@ … @@ }@@` stays one segment.
 * Top-level `@@` ends the block at the first `@@` at brace depth 0, so `;@@@@t[0][0]=…@@@@t@@`
 * splits into several {@link MaxSegment} calcs that run in order (each chunk is valid Max syntax).
 */
export function findCalcBlockEnd(source: string, openAt: number): number {
  const from = openAt + 2;
  let brace = 0;
  let inStr = false;
  let strEsc = false;
  let j = from;
  while (j < source.length) {
    const c = source[j]!;
    if (inStr) {
      if (strEsc) { strEsc = false; j++; continue; }
      if (c === "\\") { strEsc = true; j++; continue; }
      if (c === '"') { inStr = false; j++; continue; }
      j++;
      continue;
    }
    if (c === '"') { inStr = true; j++; continue; }
    if (c === "{") { brace++; j++; continue; }
    if (c === "}") { brace = Math.max(0, brace - 1); j++; continue; }
    if (brace === 0 && source.startsWith("@@", j)) {
      return j;
    }
    if (brace > 0 && source.startsWith("@@", j)) {
      // Inside `{…}`, skip nested `@@name@@` refs so they do not end the outer block.
      const idEnd = source.indexOf("@@", j + 2);
      if (idEnd >= 0) {
        const inner = source.slice(j + 2, idEnd).trim();
        if (/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(inner)) {
          j = idEnd + 2;
          continue;
        }
      }
    }
    j++;
  }
  return -1;
}

/**
 * Replace every embedded `@@name@@` inside *non-string* portions of a calc body with
 * `|name|` so the pipe-concat evaluator can substitute the variable values.  String
 * literals are left untouched.
 */
function inlineAtRefs(body: string): string {
  let out = "";
  let j = 0;
  let inStr = false;
  let strEsc = false;
  while (j < body.length) {
    const c = body[j]!;
    if (inStr) {
      if (strEsc) { strEsc = false; out += c; j++; }
      else if (c === "\\") { strEsc = true; out += c; j++; }
      else if (c === '"') { inStr = false; out += c; j++; }
      else { out += c; j++; }
      continue;
    }
    if (c === '"') { inStr = true; out += c; j++; continue; }
    if (body.startsWith("@@", j) && j + 3 < body.length) {
      const close = findCalcBlockEnd(body, j);
      if (close > j) {
        const inner = body.slice(j + 2, close).trim();
        if (/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(inner)) {
          // Convert `"str"@@name@@` → `"str"|name|` so pipe-concat evaluator substitutes the value.
          out += `\|${inner}\|`;
          j = close + 2;
          continue;
        }
      }
    }
    out += c;
    j++;
  }
  return out;
}

/** Every `@@…@@` whose inner slice contains no `@@`, respecting strings and `{…}` braces. */
function collectPlainAtPairs(body: string): { open: number; close: number }[] {
  const pairs: { open: number; close: number }[] = [];
  let brace = 0;
  let inStr = false;
  let strEsc = false;
  for (let i = 0; i < body.length - 1; i++) {
    const c = body[i]!;
    if (inStr) {
      if (strEsc) strEsc = false;
      else if (c === "\\") strEsc = true;
      else if (c === '"') inStr = false;
      continue;
    }
    if (c === '"') {
      inStr = true;
      continue;
    }
    if (c === "{") {
      brace++;
      continue;
    }
    if (c === "}") {
      brace = Math.max(0, brace - 1);
      continue;
    }
    if (brace === 0 && body.startsWith("@@", i)) {
      const close = body.indexOf("@@", i + 2);
      if (close < 0) continue;
      const inner = body.slice(i + 2, close);
      if (!inner.includes("@@")) {
        pairs.push({ open: i, close });
      }
    }
  }
  return pairs;
}

/** Prefer a pair with no other pair strictly inside it; tie-break by shortest span. */
function pickInnermostPlainPair(pairs: readonly { open: number; close: number }[]): {
  open: number;
  close: number;
} | null {
  if (pairs.length === 0) return null;
  const qStrictlyInsideP = (p: { open: number; close: number }, q: { open: number; close: number }) =>
    q.open > p.open && q.close < p.close;
  const leaves = pairs.filter((p) => !pairs.some((q) => qStrictlyInsideP(p, q)));
  const pool = leaves.length ? leaves : pairs;
  return pool.reduce((a, b) => (b.close - b.open < a.close - a.open ? b : a));
}

/**
 * Replace nested `@@…@@` spans inside a calc body (excluding string literals) with their numeric
 * / string results so `1+@@2@@+3` becomes `1+2+3` before {@link evaluateMaxNumericExpression}.
 * Uses innermost-first pairing: inner slice must not contain `@@`, so `@@2+@@3@@` expands `@@3@@`
 * before `@@2+3@@`. For whole-document `@@1+@@2@@+3@@`, wrap in `@@{…}@@` or split into separate
 * `@@…@@` blocks — {@link findCalcBlockEnd} still ends the outer block at the first top-level `@@`.
 */
export function expandNestedCalcSpansInCalcBody(
  body: string,
  maxVars: Record<string, number>,
  maxTables: MaxCalcTables,
  maxFuncs: MaxCalcUserFuncs,
  depth = 0,
): string {
  if (depth > MAX_NESTED_CALC_EXPAND) return body;
  let s = body;
  for (let iter = 0; iter < MAX_NESTED_CALC_EXPAND; iter++) {
    const pairs = collectPlainAtPairs(s);
    const p = pickInnermostPlainPair(pairs);
    if (!p) break;
    const innerRaw = s.slice(p.open + 2, p.close);
    const br = tryExtractBraceWrappedMaxBody(innerRaw);
    const innerWork = br ? br.inner : innerRaw;
    const innerExpanded = expandNestedCalcSpansInCalcBody(innerWork, maxVars, maxTables, maxFuncs, depth + 1);
    const ev = evaluateMaxNumericExpression(
      inlineAtRefs(innerExpanded),
      maxVars,
      maxTables,
      maxFuncs,
    );
    const repl = ev.ok ? ev.value : `(${ev.error})`;
    s = s.slice(0, p.open) + repl + s.slice(p.close + 2);
  }
  return s;
}

function findSameLineDelimiterEnd(source: string, openAt: number, delim: "!!"): number {
  const from = openAt + 2;
  const nl = source.indexOf("\n", from);
  const lineEnd = nl >= 0 ? nl : source.length;
  const j = source.indexOf(delim, from);
  if (j < 0 || j >= lineEnd) return -1;
  return j;
}

/** Split source into prose, `$$…$$` math, `@@…@@` calculation blocks, and `!!…!!` graph blocks (non-overlapping, left-to-right). */
export function splitMaxSource(source: string): MaxSegment[] {
  const out: MaxSegment[] = [];
  let i = 0;
  while (i < source.length) {
    if (source.startsWith("$$", i)) {
      const j = source.indexOf("$$", i + 2);
      if (j < 0) {
        out.push({ kind: "text", body: source.slice(i) });
        break;
      }
      out.push({ kind: "math", body: source.slice(i + 2, j) });
      i = j + 2;
      continue;
    }
    if (source.startsWith("@@", i)) {
      const j = findCalcBlockEnd(source, i);
      if (j < 0) {
        out.push({ kind: "text", body: source.slice(i) });
        break;
      }
      out.push({ kind: "calc", body: source.slice(i + 2, j) });
      i = j + 2;
      continue;
    }
    if (source.startsWith("!!", i)) {
      const j = findSameLineDelimiterEnd(source, i, "!!");
      if (j < 0) {
        const nl = source.indexOf("\n", i);
        const lineEnd = nl >= 0 ? nl : source.length;
        out.push({ kind: "text", body: source.slice(i, lineEnd) });
        i = lineEnd;
        continue;
      }
      out.push({ kind: "graph", body: source.slice(i + 2, j) });
      i = j + 2;
      continue;
    }
    const nextDollar = source.indexOf("$$", i);
    const nextAt = indexOfNextMaxCalcOpen(source, i);
    const nextBang = source.indexOf("!!", i);
    const candidates = [nextDollar, nextAt, nextBang].filter((n) => n >= 0);
    const end = candidates.length ? Math.min(...candidates) : source.length;
    out.push({ kind: "text", body: source.slice(i, end) });
    i = end;
  }
  return out;
}

/**
 * Build preview HTML: prose segments through {@link markdownToSafeHtmlMaxProse} (`breaks: true` so
 * single newlines match the editor; tables still use {@link markdownToSafeHtml}). Same pipeline as Markdown
 * files); `$$…$$` for display KaTeX and `$…$` or `\(…\)` for inline (single-line) KaTeX; `@@…@@`
 * blocks render as the numeric result (expression on `title`).
 * A block whose trimmed body ends with `;` is evaluated for assignments but produces no visible
 * output. Numeric `vars` and string `tables` from `name=table{r,c}` / `name[i][j]=…` persist across
 * blocks in source order. A block that is only a table name (optional trailing `;` for no output)
 * renders that grid as a GitHub-flavored markdown pipe table via {@link maxTableGridToMarkdown}.
 * A block `@@{ … }@@` (optional `;` after `}`) runs the inner `…` through the same Max pipeline first
 * (markdown, `$$`, nested `@@`, shared vars/tables) before continuing after the closing `@@`.
 * Consecutive flow prose + flow `@@` outputs are wrapped in `<p class="npp-max-md-prose npp-max-md-prose--flow">`
 * or, when an assignment row is glued to its @@ result, `<div class="…flow …flow-assign">` so layout stays one visual row.
 */
export function buildMaxPreviewHtml(source: string): string {
  const parts: string[] = ['<div class="npp-max-preview-root">'];
  const maxVars: Record<string, number> = {};
  const maxTables: MaxCalcTables = {};
  const maxFuncs: MaxCalcUserFuncs = {};
  const maxGraphs: MaxGraphs = {};
  const maxNodeColors: MaxNodeColors = {};
  const maxGraphOrientations: MaxGraphOrientations = {};
  const maxDiagrams: MaxDiagrams = {};
  const flowRun: string[] = [];
  appendMaxPreviewSegments(
    source,
    parts,
    maxVars,
    maxTables,
    maxFuncs,
    maxGraphs,
    maxNodeColors,
    maxGraphOrientations,
    maxDiagrams,
    0,
    false,
    flowRun,
  );
  parts.push("</div>");
  return parts.join("");
}

/**
 * Like {@link buildMaxPreviewHtml} but stamps every top-level rendered block element with a
 * `data-source-line="N"` attribute (0-based line index in `source`).  Used by the editor's
 * bidirectional scroll-sync logic so that clicking a rendered formula jumps to the right source
 * line and moving the caret in the editor scrolls the preview to the matching formula.
 */
export function buildMaxPreviewHtmlAnnotated(source: string): string {
  const parts: string[] = ['<div class="npp-max-preview-root">'];
  const maxVars: Record<string, number> = {};
  const maxTables: MaxCalcTables = {};
  const maxFuncs: MaxCalcUserFuncs = {};
  const maxGraphs: MaxGraphs = {};
  const maxNodeColors: MaxNodeColors = {};
  const maxGraphOrientations: MaxGraphOrientations = {};
  const maxDiagrams: MaxDiagrams = {};
  const flowRun: string[] = [];
  const segs = splitMaxSource(source);
  const lineNums = maxSegmentStartLines(source, segs);
  appendMaxPreviewSegments(
    source,
    parts,
    maxVars,
    maxTables,
    maxFuncs,
    maxGraphs,
    maxNodeColors,
    maxGraphOrientations,
    maxDiagrams,
    0,
    false,
    flowRun,
    lineNums,
  );
  parts.push("</div>");
  return parts.join("");
}

/**
 * Build markdown text that mirrors Max rendering semantics:
 * - prose is kept as-is
 * - `@@...@@` blocks are evaluated and replaced by visible value (or omitted on trailing `;`)
 * - `@@name@@` for table variables expands to a markdown pipe table
 * - `@@{ ... }@@` runs nested Max source first with shared vars/tables
 */
export function buildMaxRenderedMarkdown(source: string): string {
  const parts: string[] = [];
  const maxVars: Record<string, number> = {};
  const maxTables: MaxCalcTables = {};
  const maxFuncs: MaxCalcUserFuncs = {};
  const maxGraphs: MaxGraphs = {};
  const maxNodeColors: MaxNodeColors = {};
  const maxGraphOrientations: MaxGraphOrientations = {};
  appendMaxMarkdownSegments(
    source,
    parts,
    maxVars,
    maxTables,
    maxFuncs,
    maxGraphs,
    maxNodeColors,
    maxGraphOrientations,
    0,
    false,
  );
  return parts.join("");
}

function ensureMarkdownBlockBoundary(parts: string[]): void {
  const last = parts.length > 0 ? parts[parts.length - 1]! : "";
  if (!last) return;
  if (/\n\n$/.test(last)) return;
  if (/\n$/.test(last)) {
    parts[parts.length - 1] = `${last}\n`;
  } else {
    parts[parts.length - 1] = `${last}\n\n`;
  }
}

function appendMaxMarkdownSegments(
  source: string,
  parts: string[],
  maxVars: Record<string, number>,
  maxTables: MaxCalcTables,
  maxFuncs: MaxCalcUserFuncs,
  maxGraphs: MaxGraphs,
  maxNodeColors: MaxNodeColors,
  maxGraphOrientations: MaxGraphOrientations,
  depth: number,
  forceSuppressOutput: boolean,
): void {
  if (depth > MAX_BRACE_RECURSE) {
    if (!forceSuppressOutput) parts.push("@@{…}@@ nesting too deep");
    return;
  }
  const segs = splitMaxSource(source);
  for (const seg of segs) {
    if (seg.kind === "text") {
      if (!forceSuppressOutput) {
        parts.push(
          evalDollarMathInlines(
            evalTableCellInlines(seg.body, maxVars, maxTables, maxFuncs),
            maxVars,
            maxTables,
            maxFuncs,
          ),
        );
      }
      continue;
    }
    if (seg.kind === "math") {
      if (!forceSuppressOutput) parts.push(`$$${seg.body}$$`);
      continue;
    }
    if (seg.kind === "graph") {
      const body = seg.body.trim();
      const eqIdx = body.indexOf("=");
      if (eqIdx > 0) {
        const name = body.slice(0, eqIdx).trim();
        const edgesStr = body.slice(eqIdx + 1).trim();
        if (/^[a-zA-Z][a-zA-Z0-9]*$/.test(name)) {
          accumulateMaxGraphEdges(name, edgesStr, maxGraphs);
        }
      } else {
        const colorM = /^color\{([^}]+)\}\{([^}]*)\}$/.exec(body);
        if (colorM) {
          applyMaxNodeColors(colorM[1]!.trim(), colorM[2]!, maxNodeColors);
        } else {
          const alignM = /^align\{(vertical|horizontal)\}\{([^}]*)\}$/i.exec(body);
          if (alignM) {
            applyMaxGraphOrientation(
              alignM[1]!.toLowerCase() as "vertical" | "horizontal",
              alignM[2]!,
              maxGraphOrientations,
            );
          } else if (!forceSuppressOutput) {
            // !!ascii{name}!! or !!name!! — render as ASCII art in a code fence.
            const asciiM = /^ascii\{([a-zA-Z][a-zA-Z0-9]*)\}$/.exec(body);
            const graphName = asciiM ? asciiM[1]! : (/^[a-zA-Z][a-zA-Z0-9]*$/.test(body) ? body : null);
            if (graphName) {
              ensureMarkdownBlockBoundary(parts);
              const orientation = maxGraphOrientations[graphName] ?? "vertical";
              parts.push("```\n" + buildMaxGraphAsciiText(graphName, maxGraphs, orientation) + "\n```\n\n");
            }
          }
        }
      }
      continue;
    }
    const braced = tryExtractBraceWrappedMaxBody(seg.body);
    if (braced) {
      appendMaxMarkdownSegments(
        braced.inner,
        parts,
        maxVars,
        maxTables,
        maxFuncs,
        maxGraphs,
        maxNodeColors,
        maxGraphOrientations,
        depth + 1,
        forceSuppressOutput || braced.suppress,
      );
      continue;
    }

    const inner = seg.body.trim();
    const suppressCalcOutput = forceSuppressOutput || inner.endsWith(";");
    const tableRef = maxTableOnlyRefName(inner);
    if (tableRef && maxTables[tableRef]) {
      if (!suppressCalcOutput) {
        ensureMarkdownBlockBoundary(parts);
        parts.push(maxTableGridToMarkdown(maxTables[tableRef]!));
        parts.push("\n\n");
      }
      continue;
    }
    const drawSpec = parseMaxDrawDirective(inner);
    if (drawSpec) {
      if (!suppressCalcOutput) {
        const asciiSpec: MaxAsciiDrawSpec = { ...drawSpec, plotChar: "·" };
        ensureMarkdownBlockBoundary(parts);
        parts.push("```\n" + buildMaxAsciiPlotText(asciiSpec, maxVars, maxTables) + "\n```\n\n");
      }
      continue;
    }
    const asciiDrawSpec = parseMaxAsciiDrawDirective(inner);
    if (asciiDrawSpec) {
      if (!suppressCalcOutput) {
        ensureMarkdownBlockBoundary(parts);
        parts.push("```\n" + buildMaxAsciiPlotText(asciiDrawSpec, maxVars, maxTables) + "\n```\n\n");
      }
      continue;
    }

    const ev = evaluateMaxNumericExpression(
      inlineAtRefs(expandNestedCalcSpansInCalcBody(seg.body, maxVars, maxTables, maxFuncs)),
      maxVars,
      maxTables,
      maxFuncs,
    );
    if (!ev.ok) {
      if (!suppressCalcOutput) parts.push(`[error] ${ev.error}`);
      continue;
    }
    if (!suppressCalcOutput) parts.push(ev.value);
  }
}

/**
 * Compute the 0-based source-line number where each top-level segment in `segs` starts.
 * Used by {@link buildMaxPreviewHtmlAnnotated} to stamp `data-source-line` on rendered elements.
 * Each segment's source span is: 2-char delimiter + body + 2-char delimiter (for non-text),
 * or just the body for text segments (no surrounding delimiters).
 */
export function maxSegmentStartLines(source: string, segs: readonly MaxSegment[]): number[] {
  const result: number[] = new Array(segs.length).fill(0);
  let charPos = 0;
  let lineNum = 0;
  for (let si = 0; si < segs.length; si++) {
    const seg = segs[si]!;
    result[si] = lineNum;
    const delimLen = seg.kind === "text" ? 0 : 2; // $$, @@, !! are all 2 chars
    const spanEnd = charPos + delimLen + seg.body.length + delimLen;
    for (let k = charPos; k < spanEnd && k < source.length; k++) {
      if (source.charCodeAt(k) === 10 /* \n */) lineNum++;
    }
    charPos = spanEnd;
  }
  return result;
}

/** Keep `… =` and the following inline calc on one line (avoid wrapping between `=` and the value). */
function glueFlowPreviewEqBeforeCalc(joined: string): string {
  return joined.replace(
    /=(?:(?:\s|&nbsp;)+)(?=<span\b[^>]*\bclass\s*=\s*["'][^"']*\bnpp-max-calc\b)/gi,
    "=\u00A0",
  );
}

/**
 * Tie `name =` to the following **flow** calc: NBSP between identifier/`=` and before `<span>` so the
 * browser cannot wrap between the assignment marker and the value (NBSP after `=` alone is often insufficient).
 */
function glueFlowPreviewLvalueEqBeforeFlowCalc(joined: string): string {
  return joined.replace(
    /(\b[a-zA-Z_][a-zA-Z0-9_]*)(?:(?:\s|&nbsp;)+)=(?:(?:\s|&nbsp;)+)(?=<span\b[^>]*\bclass\s*=\s*["'][^"']*\bnpp-max-calc\b[^"']*\bnpp-max-calc--flow\b)/gi,
    "$1\u00A0=\u00A0",
  );
}

function glueFlowPreviewAssignmentFlowCalc(joined: string): string {
  return glueFlowPreviewLvalueEqBeforeFlowCalc(glueFlowPreviewEqBeforeCalc(joined));
}

/**
 * {@link wrapFlowAssignTailNowrap} keys off `\u00a0=\u00a0` before flow calcs, but
 * {@link glueFlowPreviewEqBeforeCalc} only inserts `=\u00a0` after `=`. After Markdown emphasis
 * (`</strong> = …`) {@link glueFlowPreviewLvalueEqBeforeFlowCalc} often does not run, so normalize here.
 */
function normalizeFlowEqGlueBeforeFlowCalc(joined: string): string {
  let out = joined.replace(
    /(?<!\u00a0)=\u00a0(?=<span\b[^>]*\bnpp-max-calc--flow\b)/gi,
    "\u00a0=\u00a0",
  );
  out = out.replace(/(?<!\u00a0)=\s*(?=<span\b[^>]*\bnpp-max-calc--flow\b)/gi, "\u00a0=\u00a0");
  return out;
}

/** Index in `lit` just after the `>` that closes the outer `</span>` opened before inner `contentStart`. */
function indexAfterMatchingSpanClose(lit: string, contentStart: number): number {
  let depth = 1;
  const re = /<\/?span\b/gi;
  re.lastIndex = contentStart;
  let mm: RegExpExecArray | null;
  while ((mm = re.exec(lit)) !== null) {
    const isClose = mm[0].length >= 2 && mm[0][1] === "/";
    if (isClose) depth--;
    else depth++;
    if (depth === 0) {
      const gt = lit.indexOf(">", mm.index);
      return gt >= 0 ? gt + 1 : lit.length;
    }
  }
  return lit.length;
}

/**
 * Scan left from the first `\u00a0` of `\u00a0=\u00a0<span class="…calc--flow">…` to the assignment
 * label start (bare identifier or opening `<strong>` / similar).
 */
function findFlowAssignTailLeftEdge(joined: string, eqPairStart: number): number {
  let i = eqPairStart;
  while (i > 0) {
    while (i > 0 && /[ \t\r\n\f]/.test(joined[i - 1]!)) i--;
    if (i === 0) return 0;
    if (joined[i - 1] === ">") {
      const tagOpen = joined.lastIndexOf("<", i - 1);
      if (tagOpen < 0) return i;
      const tag = joined.slice(tagOpen, i);
      if (/^<\/(?:strong|b|em|i|mark)\s*>$/i.test(tag)) {
        i = tagOpen;
        continue;
      }
      return tagOpen;
    }
    let j = i;
    while (j > 0 && /[a-zA-Z0-9_]/.test(joined[j - 1]!)) j--;
    return j;
  }
  return 0;
}

/**
 * NBSP glue is not enough in some browsers (break allowed before `inline-block` calc). Wrap
 * `label\u00A0=\u00A0<span class="…calc--flow">…</span>` in a nowrap span (label may be Markdown
 * emphasis); KaTeX auto-render must not mutate this subtree ({@link renderMaxPreviewMath}).
 */
function wrapFlowAssignTailNowrap(joined: string): string {
  const reFlowEq = /\u00a0=\u00a0<span\b[^>]*\bnpp-max-calc--flow\b[^>]*>/i;
  const m = reFlowEq.exec(joined);
  if (!m || m.index === undefined) return joined;
  const eqPairStart = m.index;
  const spanTagStart = eqPairStart + "\u00a0=\u00a0".length;
  const gtIdx = joined.indexOf(">", spanTagStart);
  if (gtIdx < 0) return joined;
  const afterOuterGt = gtIdx + 1;
  const endExclusive = indexAfterMatchingSpanClose(joined, afterOuterGt);
  const idStart = findFlowAssignTailLeftEdge(joined, eqPairStart);
  const slice = joined.slice(idStart, endExclusive);
  return `${joined.slice(0, idStart)}<span class="npp-max-flow-assign-tail">${slice}</span>${joined.slice(endExclusive)}`;
}

/** Trim ASCII whitespace only — `String.trim()` also strips NBSP (U+00A0) and breaks glue checks. */
function trimAsciiWs(s: string): string {
  return s.replace(/^[ \t\r\n\f\v]+|[ \t\r\n\f\v]+$/g, "");
}

/** True when assignment lead HTML is safe to merge inside the following flow-calc `<span>`. */
function isMergeableAssignmentLeadForFlowCalc(lead: string): boolean {
  const t = trimAsciiWs(lead).replace(/(?:&nbsp;)+$/gi, "");
  if (!t.includes("<")) {
    return /^[a-zA-Z][a-zA-Z0-9_]*\u00a0=\u00a0$/u.test(t);
  }
  return (
    /^<strong>[^<]*<\/strong>\u00a0=\u00a0$/u.test(t) ||
    /^<em>[^<]*<\/em>\u00a0=\u00a0$/u.test(t)
  );
}

/**
 * If the flush buffer is exactly `[assignment lead, flow calc span]`, move the lead inside the
 * calc span so the label and numeric result share one nowrap subtree (no sibling boundary to wrap).
 */
function tryMergeAtomicAssignmentLeadWithFollowingFlowCalc(flowRun: string[]): void {
  if (flowRun.length !== 2) return;
  const lead = flowRun[0]!;
  const calc = flowRun[1]!;
  if (/<span\b[^>]*\bnpp-max-calc\b/i.test(lead)) return;
  if (!isMergeableAssignmentLeadForFlowCalc(lead)) return;
  const m = /^(\s*)(<span\b[^>]*\bnpp-max-calc\b[^>]*\bnpp-max-calc--flow\b[^>]*>)/i.exec(calc);
  if (!m) return;
  const insertPos = m[1]!.length + m[2]!.length;
  const merged =
    calc.slice(0, insertPos) +
    `<span class="npp-max-calc-assign-prefix">` +
    lead +
    `</span>` +
    calc.slice(insertPos);
  flowRun.length = 0;
  flowRun.push(merged);
}

/** Emit buffered flow prose + flow calcs as one `<p>` / `<div>` (Markdown-style inline line). */
function flushMaxPreviewFlowRun(
  parts: string[],
  flowRun: string[],
  lineAttr = "",
  assignmentWholeLineFromSource = false,
): void {
  if (flowRun.length === 0) return;
  tryMergeAtomicAssignmentLeadWithFollowingFlowCalc(flowRun);
  const mergedAssignLine =
    flowRun.length === 1 &&
    flowRun[0]!.includes("npp-max-calc-assign-prefix") &&
    /^\s*<span class="npp-max-calc\b/.test(flowRun[0]!);

  let joined = normalizeFlowEqGlueBeforeFlowCalc(glueFlowPreviewAssignmentFlowCalc(flowRun.join("")));
  if (mergedAssignLine) {
    joined = `<span class="npp-max-flow-assign-tail">${joined}</span>`;
  } else if (assignmentWholeLineFromSource && flowRun.length >= 2) {
    joined = `<span class="npp-max-flow-assign-tail">${joined}</span>`;
  } else {
    joined = wrapFlowAssignTailNowrap(joined);
  }
  if (flowRun.length === 1 && /^\s*<span class="npp-max-calc\b/.test(flowRun[0]!)) {
    const assignRow = mergedAssignLine || joined.includes("npp-max-flow-assign-tail");
    if (assignRow) {
      parts.push(
        `<div class="npp-max-md-prose npp-max-md-prose--flow npp-max-md-prose--flow-assign"${lineAttr}>${joined}</div>`,
      );
    } else {
      parts.push(joined);
    }
  } else {
    const hasAssignTail = joined.includes("npp-max-flow-assign-tail");
    if (hasAssignTail) {
      parts.push(
        `<div class="npp-max-md-prose npp-max-md-prose--flow npp-max-md-prose--flow-assign"${lineAttr}>${joined}</div>`,
      );
    } else {
      parts.push(`<p class="npp-max-md-prose npp-max-md-prose--flow"${lineAttr}>${joined}</p>`);
    }
  }
  flowRun.length = 0;
}

/**
 * Starting from index `from` (a `!!name!!` graph-reference segment), returns the index
 * of the last consecutive graph-reference on the same source line — i.e., only `text`
 * segments containing no newline characters are allowed between graph-ref segments.
 * Returns `from` when the segment at `from` is solo (no peer on the same line).
 */
function graphRefRowEnd(segs: readonly MaxSegment[], from: number): number {
  let last = from;
  let k = from + 1;
  while (k < segs.length) {
    const s = segs[k]!;
    if (s.kind === "graph" && !s.body.includes("=") && /^[a-zA-Z][a-zA-Z0-9]*$/.test(s.body.trim())) {
      last = k; k++; continue;
    }
    if (s.kind === "text" && !/[\r\n]/.test(s.body)) { k++; continue; }
    break;
  }
  return last;
}

/**
 * Resolve every `@@…@@` inline inside pipe table blocks within `source`.
 * Pipe table = consecutive lines whose trimmed content starts with `|`.
 * We evaluate each `@@expr@@` with the current vars/tables/funcs and splice
 * the results back so the entire table stays as a single text segment for
 * markdown rendering.
 */
function evalTableCellInlines(
  source: string,
  vars: Record<string, number>,
  tables: MaxCalcTables,
  funcs: MaxCalcUserFuncs,
): string {
  const lines = source.split("\n");
  let inTable = false;
  let tableStart = 0;

  for (let i = 0; i < lines.length; i++) {
    if (lines[i]!.trimStart().startsWith("|")) {
      if (!inTable) { tableStart = i; inTable = true; }
    } else {
      if (inTable) {
        for (let t = tableStart; t < i; t++) {
          lines[t] = resolveCellCalcs(lines[t]!, vars, tables, funcs);
        }
        inTable = false;
      }
    }
  }
  if (inTable) {
    for (let t = tableStart; t < lines.length; t++) {
      lines[t] = resolveCellCalcs(lines[t]!, vars, tables, funcs);
    }
  }
  return lines.join("\n");
}

/** Resolve all `@@expr@@` occurrences inside one pipe table line. */
function resolveCellCalcs(
  line: string,
  vars: Record<string, number>,
  tables: MaxCalcTables,
  funcs: MaxCalcUserFuncs,
): string {
  const parts: string[] = [];
  let i = 0;
  while (i < line.length - 1) {
    if (line.startsWith("@@", i)) {
      const close = line.indexOf("@@", i + 2);
      if (close < 0) break;
      const expr = line.slice(i + 2, close).trim();
      if (expr) {
        const ev = evaluateMaxNumericExpression(
          inlineAtRefs(expandNestedCalcSpansInCalcBody(line.slice(i + 2, close), vars, tables, funcs)),
          vars,
          tables,
          funcs,
        );
        parts.push(ev.ok ? ev.value : `[${ev.error}]`);
      } else {
        parts.push(line.slice(i, close + 2));
      }
      i = close + 2;
    } else {
      const next = line.indexOf("@@", i);
      if (next < 0) { parts.push(line.slice(i)); i = line.length; }
      else { parts.push(line.slice(i, next)); i = next; }
    }
  }
  if (i < line.length) parts.push(line.slice(i));
  return parts.join("");
}

function countBackslashesBefore(s: string, j: number): number {
  let n = 0;
  for (let k = j - 1; k >= 0 && s[k] === "\\"; k--) n++;
  return n;
}

function isUnescapedDollar(s: string, j: number): boolean {
  return countBackslashesBefore(s, j) % 2 === 0;
}

/** First index `j >= from` where `$$` starts and the first `$` is not escaped. */
function findNextUnescapedDoubleDollar(s: string, from: number): number {
  for (let j = from; j < s.length - 1; j++) {
    if (s[j] === "$" && s[j + 1] === "$" && isUnescapedDollar(s, j)) return j;
  }
  return -1;
}

/** First unescaped `$` at or after `from` (used to close inline `$…$`). */
function findFirstUnescapedDollarFrom(s: string, from: number): number {
  for (let j = from; j < s.length; j++) {
    if (s[j] === "$" && isUnescapedDollar(s, j)) return j;
  }
  return -1;
}

function sourceLineBoundsAt(source: string, pos: number): { start: number; end: number } {
  const nlBefore = source.lastIndexOf("\n", pos - 1);
  const start = nlBefore < 0 ? 0 : nlBefore + 1;
  const nlAfter = source.indexOf("\n", pos);
  const end = nlAfter < 0 ? source.length : nlAfter;
  return { start, end };
}

/** True when `pos` lies on a GitHub-style pipe table row (trimmed line starts with `|`). */
function isPipeTableLineAt(source: string, pos: number): boolean {
  const { start, end } = sourceLineBoundsAt(source, pos);
  return source.slice(start, end).trimStart().startsWith("|");
}

/**
 * Smallest index `>= from` where `@@` starts a Max calc block — excluding `@@` that appear
 * inside `$$…$$` or `$…$` so inline math can host `@@expr@@` evaluated at prose render time,
 * and excluding `@@…@@` inside pipe-table rows (resolved later with current vars).
 */
function indexOfNextMaxCalcOpen(source: string, from: number): number {
  let p = from;
  while (p < source.length - 1) {
    if (source.startsWith("$$", p)) {
      const close = findNextUnescapedDoubleDollar(source, p + 2);
      if (close < 0) return -1;
      p = close + 2;
      continue;
    }
    if (source[p] === "$" && isUnescapedDollar(source, p)) {
      const close = findFirstUnescapedDollarFrom(source, p + 1);
      if (close < 0) return -1;
      p = close + 1;
      continue;
    }
    if (source.startsWith("@@", p)) {
      if (isPipeTableLineAt(source, p)) {
        const close = source.indexOf("@@", p + 2);
        if (close < 0) {
          p++;
          continue;
        }
        p = close + 2;
        continue;
      }
      return p;
    }
    p++;
  }
  return -1;
}

/**
 * Evaluate each `@@…@@` inside inline `$…$` spans (per line). Skips `$$…$$` display regions.
 * Must run when rendering a text segment so prior `@@` assignments in the same document are visible.
 */
function evalDollarMathInlines(
  source: string,
  vars: Record<string, number>,
  tables: MaxCalcTables,
  funcs: MaxCalcUserFuncs,
): string {
  const lines = source.split("\n");
  for (let li = 0; li < lines.length; li++) {
    lines[li] = evalDollarMathInlinesInLine(lines[li]!, vars, tables, funcs);
  }
  return lines.join("\n");
}

function evalDollarMathInlinesInLine(
  line: string,
  vars: Record<string, number>,
  tables: MaxCalcTables,
  funcs: MaxCalcUserFuncs,
): string {
  let out = "";
  let i = 0;
  while (i < line.length) {
    if (line.startsWith("$$", i)) {
      const close = findNextUnescapedDoubleDollar(line, i + 2);
      if (close < 0) {
        out += line.slice(i);
        break;
      }
      out += line.slice(i, close + 2);
      i = close + 2;
      continue;
    }
    if (line[i] === "$" && isUnescapedDollar(line, i)) {
      const close = findFirstUnescapedDollarFrom(line, i + 1);
      if (close < 0) {
        out += line.slice(i);
        break;
      }
      const inner = line.slice(i + 1, close);
      out += "$" + resolveCellCalcs(inner, vars, tables, funcs) + "$";
      i = close + 1;
      continue;
    }
    out += line[i]!;
    i++;
  }
  return out;
}

function appendMaxPreviewSegments(
  source: string,
  parts: string[],
  maxVars: Record<string, number>,
  maxTables: MaxCalcTables,
  maxFuncs: MaxCalcUserFuncs,
  maxGraphs: MaxGraphs,
  maxNodeColors: MaxNodeColors,
  maxGraphOrientations: MaxGraphOrientations,
  maxDiagrams: MaxDiagrams,
  depth: number,
  forceSuppressOutput: boolean,
  flowRun: string[],
  lineNums: readonly number[] | null = null,
): void {
  if (depth > MAX_BRACE_RECURSE) {
    flushMaxPreviewFlowRun(parts, flowRun);
    parts.push(
      '<span class="npp-max-calc npp-max-calc--err"><span class="npp-max-calc-msg">@@{…}@@ nesting too deep</span></span>',
    );
    return;
  }
  const segs = splitMaxSource(source);
  const calcFlow = maxCalcFlowLayoutByIndex(segs);

  // Track the data-source-line attribute for the active flow run.  It is set when the first
  // item is added to `flowRun` and reset when the run is flushed.
  let flowRunLineAttr = "";
  let flowRunAssignmentWholeLine = false;
  const captureFlowLine = (si: number): void => {
    if (flowRun.length === 0 && lineNums) {
      flowRunLineAttr = ` data-source-line="${lineNums[si]}"`;
    }
  };
  const flushFlow = (): void => {
    flushMaxPreviewFlowRun(parts, flowRun, flowRunLineAttr, flowRunAssignmentWholeLine);
    flowRunAssignmentWholeLine = false;
    flowRunLineAttr = "";
  };

  const la = (si: number): string =>
    lineNums ? ` data-source-line="${lineNums[si]}"` : "";

  for (let si = 0; si < segs.length; si++) {
    const seg = segs[si]!;
    if (seg.kind === "graph") {
      // Graph segments are always block-level; flush any in-progress flow run first.
      flushFlow();
      const body = seg.body.trim();

      // ── Diagram DSL detection (draw.io) ──────────────────────────
      const diagDef = isDiagramDefBody(body);
      if (diagDef) {
        if (!maxDiagrams[diagDef]) {
          maxDiagrams[diagDef] = { name: diagDef, nodes: [], edges: [] };
        }
        // Collect all subsequent text segments until the !!diagram=name!! reference
        // The diagram body spans from this segment's start to the reference
        const diagramBodyParts: string[] = [];
        let refFound = false;
        let refName = "";
        for (let di = si + 1; di < segs.length; di++) {
          const segInner = segs[di]!;
          const inner = segInner.body;
          if (segInner.kind === "graph") {
            const rb = isDiagramRefBody(inner.trim());
            if (rb && rb.name === diagDef) {
              refFound = true;
              refName = rb.name;
              si = di;
              break;
            }
          }
          // `@@…@@` inside a label splits into calc segments; splice back so the DSL line is whole.
          if (segInner.kind === "calc") {
            diagramBodyParts.push(`@@${inner}@@`);
            continue;
          }
          if (segInner.kind === "text" && !inner.includes("!!")) {
            diagramBodyParts.push(inner);
          }
        }
        if (refFound && !forceSuppressOutput) {
          const parsed = parseDiagramBody(diagDef, diagramBodyParts.join("\n"));
          maxDiagrams[diagDef] = parsed;
          parts.push(buildDiagramSvgSync(parsed));
        }
        continue;
      }

      const diagRef = isDiagramRefBody(body);
      if (diagRef) {
        if (!forceSuppressOutput && maxDiagrams[diagRef.name]) {
          parts.push(buildDiagramSvgSync(maxDiagrams[diagRef.name]));
        }
        continue;
      }

      // ── Legacy graph syntax (!!name=edges!! / !!name!! / !!color{…}{…}!!) ──
      const eqIdx = body.indexOf("=");
      if (eqIdx > 0) {
        // Definition: !!name=a->b->c!!  — accumulate edges, no visible output.
        const name = body.slice(0, eqIdx).trim();
        const edgesStr = body.slice(eqIdx + 1).trim();
        if (/^[a-zA-Z][a-zA-Z0-9]*$/.test(name)) {
          accumulateMaxGraphEdges(name, edgesStr, maxGraphs);
        }
      } else {
        const colorM = /^color\{([^}]+)\}\{([^}]*)\}$/.exec(body);
        if (colorM) {
          // !!color{X}{a,b,c}!! — apply colors, no visible output.
          applyMaxNodeColors(colorM[1]!.trim(), colorM[2]!, maxNodeColors);
        } else {
          const alignM = /^align\{(vertical|horizontal)\}\{([^}]*)\}$/i.exec(body);
          if (alignM) {
            // !!align{horizontal}{g,q,s}!! — switch named graph layout orientation.
            applyMaxGraphOrientation(
              alignM[1]!.toLowerCase() as "vertical" | "horizontal",
              alignM[2]!,
              maxGraphOrientations,
            );
          } else {
            const asciiM = /^ascii\{([a-zA-Z][a-zA-Z0-9]*)\}$/.exec(body);
            if (asciiM) {
              // !!ascii{name}!! — render named graph as ASCII box diagram.
              if (!forceSuppressOutput) {
                parts.push(buildMaxGraphAscii(asciiM[1]!, maxGraphs, maxGraphOrientations[asciiM[1]!] ?? "vertical"));
              }
            } else if (/^[a-zA-Z][a-zA-Z0-9]*$/.test(body)) {
              // Reference: !!name!! — render SVG, with horizontal row when same-line peers exist.
              if (!forceSuppressOutput) {
                const rowEnd = graphRefRowEnd(segs, si);
                if (rowEnd > si) {
                  const svgs: string[] = [
                    buildMaxGraphSvg(
                      body,
                      maxGraphs,
                      maxNodeColors,
                      maxGraphOrientations[body] ?? "vertical",
                    ),
                  ];
                  for (let k = si + 1; k <= rowEnd; k++) {
                    const s = segs[k]!;
                    if (s.kind === "graph") {
                      const n2 = s.body.trim();
                      if (/^[a-zA-Z][a-zA-Z0-9]*$/.test(n2)) {
                        svgs.push(
                          buildMaxGraphSvg(
                            n2,
                            maxGraphs,
                            maxNodeColors,
                            maxGraphOrientations[n2] ?? "vertical",
                          ),
                        );
                      }
                    }
                  }
                  parts.push(`<div class="npp-max-graph-row"${la(si)}>${svgs.join("")}</div>`);
                  si = rowEnd;
                } else {
                  parts.push(
                    buildMaxGraphSvg(
                      body,
                      maxGraphs,
                      maxNodeColors,
                      maxGraphOrientations[body] ?? "vertical",
                    ),
                  );
                }
              }
            }
          }
        }
      }
      continue;
    }
    if (seg.kind === "text") {
      if (!seg.body) continue;
      if (!forceSuppressOutput) {
        const textForMd = evalDollarMathInlines(
          evalTableCellInlines(seg.body, maxVars, maxTables, maxFuncs),
          maxVars,
          maxTables,
          maxFuncs,
        );
        // If we're already in a flow run and this text starts with same-line prose then later
        // breaks into new paragraphs, keep the same-line prefix in flow (e.g. `x @@a@@ a\n\nb`)
        // and render only the tail as block prose.
        if (flowRun.length > 0) {
          const firstNlAt = seg.body.search(/\r?\n/);
          if (firstNlAt > 0) {
            const lead = textForMd.slice(0, firstNlAt);
            const tail = textForMd.slice(firstNlAt);
            const leadSrc = seg.body.slice(0, firstNlAt);
            captureFlowLine(si);
            const nextSegLead = si + 1 < segs.length ? segs[si + 1] : undefined;
            const assignmentLead =
              nextSegLead?.kind === "calc" &&
              (calcFlow[si + 1] ?? false) &&
              maxFlowSourceAssignmentWholeLine(leadSrc);
            const atomicLead = assignmentLead ? maxFlowAssignmentLeadHtmlFromSource(leadSrc) : null;
            let leadHtml: string;
            if (atomicLead !== null) {
              leadHtml = atomicLead;
              flowRunAssignmentWholeLine = true;
            } else {
              leadHtml = markdownToSafeHtmlMaxProse(lead);
              leadHtml = unwrapMaxFlowMarkdownSingleParagraph(leadHtml);
              if (assignmentLead) {
                flowRunAssignmentWholeLine = true;
                leadHtml = stripTrailingMarkedHardBreaks(leadHtml);
              }
            }
            flowRun.push(atomicLead !== null ? leadHtml : preserveMaxFlowEdgeSpaces(leadSrc, leadHtml));
            flushFlow();
            const tailHtml = markdownToSafeHtmlMaxProse(tail);
            parts.push(`<div class="npp-max-md-prose"${la(si)}>${tailHtml}</div>`);
            continue;
          }
        }

        const previous = si > 0 ? segs[si - 1] : undefined;
        const next = si + 1 < segs.length ? segs[si + 1] : undefined;
        const startsAfterCalcLineBreak =
          previous?.kind === "calc" && /^\s*\r?\n/.test(seg.body);
        const preservePostCalcBreak = startsAfterCalcLineBreak && next?.kind !== "calc";
        const startsWithPostCalcParagraphBreak =
          previous?.kind === "calc" && maxTextSegmentStartsWithParagraphBreak(seg.body);
        const flow = maxTextSegmentUsesFlowLayout(seg.body) && !preservePostCalcBreak;
        const flowCls = flow ? " npp-max-md-prose--flow" : "";
        const assignmentFlowCalc =
          !!flow &&
          next?.kind === "calc" &&
          (calcFlow[si + 1] ?? false) &&
          maxFlowSourceAssignmentWholeLine(seg.body);
        const atomicAssignmentHtml = assignmentFlowCalc ? maxFlowAssignmentLeadHtmlFromSource(seg.body) : null;
        let innerHtml: string;
        if (atomicAssignmentHtml !== null) {
          innerHtml = atomicAssignmentHtml;
        } else {
          innerHtml = markdownToSafeHtmlMaxProse(textForMd);
          if (flow) innerHtml = unwrapMaxFlowMarkdownSingleParagraph(innerHtml);
        }
        if (flow) {
          if (startsWithPostCalcParagraphBreak) {
            flushFlow();
            parts.push(`<div class="npp-max-md-prose"${la(si)}><p><br></p></div>`);
          }
          captureFlowLine(si);
          if (assignmentFlowCalc) {
            flowRunAssignmentWholeLine = true;
            if (atomicAssignmentHtml === null) {
              innerHtml = stripTrailingMarkedHardBreaks(innerHtml);
            }
          }
          flowRun.push(
            atomicAssignmentHtml !== null ? innerHtml : preserveMaxFlowEdgeSpaces(seg.body, innerHtml),
          );
        } else {
          flushFlow();
          if (previous?.kind === "calc") {
            innerHtml = preservePostCalcLeadingBlankLine(seg.body, innerHtml);
          }
          parts.push(`<div class="npp-max-md-prose${flowCls}"${la(si)}>${innerHtml}</div>`);
        }
      }
    } else if (seg.kind === "math") {
      if (!forceSuppressOutput) {
        flushFlow();
        parts.push(`<div class="npp-max-math"${la(si)}>$$${seg.body}$$</div>`);
      }
    } else {
      const braced = tryExtractBraceWrappedMaxBody(seg.body);
      if (braced) {
        flushFlow();
        appendMaxPreviewSegments(
          braced.inner,
          parts,
          maxVars,
          maxTables,
          maxFuncs,
          maxGraphs,
          maxNodeColors,
          maxGraphOrientations,
          maxDiagrams,
          depth + 1,
          forceSuppressOutput || braced.suppress,
          [],
          null, // nested blocks are not individually annotated
        );
        continue;
      }
      const inner = seg.body.trim();
      const suppressCalcOutput = forceSuppressOutput || inner.endsWith(";");
      const srcEsc = escapeHtml(inner);
      const tip = srcEsc ? ` title="${srcEsc}"` : "";
      const tableRef = maxTableOnlyRefName(inner);
      if (tableRef && maxTables[tableRef]) {
        if (!suppressCalcOutput) {
          flushFlow();
          const md = maxTableGridToMarkdown(maxTables[tableRef]!);
          const innerHtml = markdownToSafeHtml(md);
          parts.push(`<div class="npp-max-md-table"${la(si)}${tip}>${innerHtml}</div>`);
        }
        continue;
      }
      const drawSpec = parseMaxDrawDirective(inner);
      if (drawSpec) {
        if (!suppressCalcOutput) {
          flushFlow();
          parts.push(buildMaxDrawSvg(drawSpec, maxVars, maxTables));
        }
        continue;
      }
      const asciiDrawSpec = parseMaxAsciiDrawDirective(inner);
      if (asciiDrawSpec) {
        if (!suppressCalcOutput) {
          flushFlow();
          parts.push(buildMaxAsciiPlot(asciiDrawSpec, maxVars, maxTables));
        }
        continue;
      }
      const ev = evaluateMaxNumericExpression(
        inlineAtRefs(expandNestedCalcSpansInCalcBody(seg.body, maxVars, maxTables, maxFuncs)),
        maxVars,
        maxTables,
        maxFuncs,
      );
      const flowCls = calcFlow[si] ? " npp-max-calc--flow" : "";
      const isFlowCalc = calcFlow[si] ?? false;
      if (!ev.ok) {
        const errEsc = escapeHtml(ev.error);
        const html = `<span class="npp-max-calc${flowCls} npp-max-calc--err"${la(si)}${tip}><span class="npp-max-calc-msg">${errEsc}</span></span>`;
        if (isFlowCalc) {
          captureFlowLine(si);
          flowRun.push(html);
        } else {
          flushFlow();
          parts.push(html);
        }
      } else if (!suppressCalcOutput) {
        const valEsc = escapeHtml(ev.value);
        const html = `<span class="npp-max-calc${flowCls}"${la(si)}${tip}><strong class="npp-max-calc-val">${valEsc}</strong></span>`;
        if (isFlowCalc) {
          captureFlowLine(si);
          flowRun.push(html);
        } else {
          flushFlow();
          parts.push(html);
        }
      }
    }
  }
  flushFlow();
}

/** After `innerHTML = buildMaxPreviewHtml(…)` on a Max preview root: math, PDF rasters, HTML hex iframes. */
export function hydrateMaxPreviewRoot(root: HTMLElement): void {
  renderMaxPreviewMath(root);
  mountMaxPdfRasterImages(root);
  mountMaxHtmlHexIframes(root);
  mountMaxHexMediaFromLinks(root);
}

export function renderMaxPreviewMath(el: HTMLElement): void {
  renderMathInElement(el, {
    delimiters: [
      { left: "$$", right: "$$", display: true },
      { left: "\\(", right: "\\)", display: false },
      { left: "$", right: "$", display: false },
    ],
    throwOnError: false,
    strict: "ignore",
    ignoredTags: [...KATEX_AUTO_RENDER_IGNORED_TAGS],
    /** Skip KaTeX inside embedded HTML (`Insert HTML`); avoids `$` in user markup becoming math. */
    ignoredClasses: [
      "npp-max-html-embed",
      "npp-max-html-hex-embed",
      "npp-max-video-hex-embed",
      "npp-max-audio-hex-embed",
      /** Evaluated @@ spans are not math; `$` in expressions must not run KaTeX inside results. */
      "npp-max-calc",
      /** Assignment + flow calc row is layout-critical; leave DOM untouched for auto-render. */
      "npp-max-flow-assign-tail",
      /** Label merged inside flow calc; `$` in identifiers must not invoke KaTeX here. */
      "npp-max-calc-assign-prefix",
    ],
  });
}

const MAX_HTML_HEX_HREF_PREFIX = "npp-max-html:1;";
const MAX_HTML_HEX_HREF_RE = /^npp-max-html:1;([0-9a-f]+)$/i;

/**
 * Turn `[…](<npp-max-html:1;hex>)` anchors into sandboxed iframes (`srcdoc` from decoded UTF-8) and
 * size height to document scroll height (capped so flex layout does not blow up).
 */
export function mountMaxHtmlHexIframes(root: HTMLElement): void {
  const links = root.querySelectorAll<HTMLAnchorElement>(`a[href^="${MAX_HTML_HEX_HREF_PREFIX}"]`);
  const pane = root.closest(".npp-max-preview-body") as HTMLElement | null;
  const capBase = pane?.clientHeight ?? root.parentElement?.clientHeight ?? 1200;
  const heightCap = Math.min(32_000, Math.max(1200, Math.round(capBase * 6)));

  links.forEach((a) => {
    const href = a.getAttribute("href")?.trim() ?? "";
    const m = MAX_HTML_HEX_HREF_RE.exec(href);
    if (!m?.[1]) return;
    const bytes = hex64ToBytes(m[1]);
    if (!bytes?.length) return;
    let html: string;
    try {
      html = new TextDecoder("utf-8", { fatal: false }).decode(bytes);
    } catch {
      return;
    }
    if (!html.trim()) return;

    const wrap = document.createElement("div");
    wrap.className = "npp-max-html-hex-embed";

    const iframe = document.createElement("iframe");
    iframe.className = "npp-max-html-hex-iframe";
    iframe.title = a.textContent?.trim() || "Embedded HTML";
    iframe.setAttribute(
      "sandbox",
      "allow-same-origin allow-popups-to-escape-sandbox allow-forms allow-modals allow-downloads",
    );
    iframe.referrerPolicy = "no-referrer";
    iframe.loading = "lazy";
    iframe.srcdoc = html;

    const applyHeight = (): void => {
      try {
        const doc = iframe.contentDocument;
        const h = Math.max(
          doc?.documentElement?.scrollHeight ?? 0,
          doc?.body?.scrollHeight ?? 0,
        );
        if (h >= 24) iframe.style.height = `${Math.min(h + 24, heightCap)}px`;
      } catch {
        /* ignore */
      }
    };

    iframe.addEventListener("load", () => {
      applyHeight();
      try {
        const innerDoc = iframe.contentDocument;
        const el = innerDoc?.documentElement ?? innerDoc?.body;
        if (el && typeof ResizeObserver !== "undefined") {
          const ro = new ResizeObserver(() => applyHeight());
          ro.observe(el);
        }
      } catch {
        /* ignore */
      }
    });

    wrap.appendChild(iframe);
    a.replaceWith(wrap);
  });
}

const MAX_VIDEO_HEX_HREF_RE = /^npp-max-video:1;([0-9a-f]+)$/i;
const MAX_AUDIO_HEX_HREF_RE = /^npp-max-audio:1;([0-9a-f]+)$/i;

/** Revoke blob: URLs created for hex-linked video/audio before rebuilding preview HTML. */
export function revokeMaxHexMediaBlobUrls(root: HTMLElement): void {
  root.querySelectorAll<HTMLVideoElement | HTMLAudioElement>("video[data-npp-max-hex-blob],audio[data-npp-max-hex-blob]").forEach((el) => {
    const u = el.getAttribute("src");
    if (u?.startsWith("blob:")) URL.revokeObjectURL(u);
  });
}

/**
 * Replace `[…](<npp-max-video:1;hex>)` / `[…](<npp-max-audio:1;hex>)` with `<video>` / `<audio>` backed by
 * `URL.createObjectURL` (WebM bytes from toolbar recording or future importers).
 */
export function mountMaxHexMediaFromLinks(root: HTMLElement): void {
  const mountOne = (a: HTMLAnchorElement, kind: "video" | "audio"): void => {
    const href = a.getAttribute("href")?.trim() ?? "";
    const m =
      kind === "video" ? MAX_VIDEO_HEX_HREF_RE.exec(href) : MAX_AUDIO_HEX_HREF_RE.exec(href);
    if (!m?.[1]) return;
    const bytes = hex64ToBytes(m[1]);
    if (!bytes?.length) return;
    const blob = new Blob([bytes], { type: kind === "video" ? "video/webm" : "audio/webm" });
    const url = URL.createObjectURL(blob);
    const el = document.createElement(kind);
    el.controls = true;
    if (kind === "video") (el as HTMLVideoElement).playsInline = true;
    el.setAttribute("preload", "metadata");
    el.src = url;
    el.dataset.nppMaxHexBlob = "1";
    el.className =
      kind === "video" ? "npp-max-embed-video npp-max-embed-video--hex" : "npp-max-embed-audio npp-max-embed-audio--hex";
    el.title = a.textContent?.trim() || (kind === "video" ? "Video" : "Audio");
    const wrap = document.createElement("div");
    wrap.className = kind === "video" ? "npp-max-video-hex-embed" : "npp-max-audio-hex-embed";
    wrap.appendChild(el);
    a.replaceWith(wrap);
  };

  root.querySelectorAll<HTMLAnchorElement>(`a[href^="${MAX_MAX_VIDEO_HEX_PREFIX}"]`).forEach((a) => {
    mountOne(a, "video");
  });
  root.querySelectorAll<HTMLAnchorElement>(`a[href^="${MAX_MAX_AUDIO_HEX_PREFIX}"]`).forEach((a) => {
    mountOne(a, "audio");
  });
}

export { mountMaxPdfRasterImages } from "./maxPdfPageRaster.js";
